/**
 * The protocol version, and what to do about a peer that carries a different one.
 *
 * Two players on the same join code can be on different builds, because a deploy does not reach
 * open tabs. Without a version byte that shows up as a snapshot decoded against the wrong layout,
 * which looks like a physics bug and is not one. So the version rides every frame and is checked
 * twice: at the handshake, where a mismatch refuses the peer with a sentence naming the cause, and
 * again per frame, where it throws rather than parses.
 *
 * Bump `PROTOCOL_VERSION` whenever a payload layout changes. Move `MIN_COMPATIBLE_VERSION` up with
 * it only when the older layout genuinely cannot be read any more.
 */

export const PROTOCOL_VERSION = 11;

/**
 * The oldest peer version this build still understands.
 *
 * Moved to 2 with the snapshot header: version 1 wrote nine bytes before the first section and 2
 * writes eleven, so a version 1 peer reads the input sequence as a section count and everything
 * after it as garbage.
 *
 * Moved to 3 with the hello's free-seat byte: a version 2 hello is nine bytes and this build needs
 * ten, and a peer that cannot say how many seats it has left cannot be turned away from a full
 * session with a sentence explaining why. There is no reading either old layout, only refusing it.
 *
 * Moved to 4 with the lobby's rules tag: a version 3 peer throws on a tag it has never seen, so a
 * host that has turned friendly fire on would drop every older guest as it announced it. Refusing
 * them at the handshake says what happened instead.
 *
 * Moved to 5 with the lobby's name tag, for the same reason: a version 4 host throws on tag 10, so
 * a guest saying what it is called would drop itself the moment it arrived.
 *
 * Moved to 6 with the character sheet. Two layouts changed at once: tag 11 is new, and a roster
 * seat now carries a sheet after its name, so a version 5 peer reads the next seat's player id out
 * of somebody else's stat block and hands the room a player list that is wrong rather than short.
 * A perk is named on the wire by its position in `PERK_ORDER`, which makes that list part of the
 * layout too: inserting or reordering a perk means bumping both constants again.
 *
 * Moved to 7 with the measured link quality. The roster's quality byte used to hold one word and
 * now packs a path in its low two bits and a latency band in the next two, so a version 6 peer
 * reads every seat's byte as a code it has no word for and shows a room where nobody is connected.
 * The seat is still the same length, so this one is a lobby that lies rather than a roster that
 * cannot be parsed, and lying about who is connected is the failure this file exists to prevent.
 *
 * Moved to 8 with the hello's seat claim. The hello grew from ten bytes to eighteen, so a version 7
 * peer reads a claim it has no field for and a version 8 peer reads a hello eight bytes short. As
 * with the free-seat byte there is no reading the old layout, only refusing it.
 *
 * Moved to 9 with the own-gear record. A snapshot names its kinds by a tag byte, so a version 8
 * guest meets kind 7 with no field list to lay it out against and cannot find where that section
 * ends, which takes every record after it with it. A new kind is a layout change like any other.
 *
 * Moved to 10 with the own-status record, for exactly that reason, and with the use key that fills
 * it: kind 8 is new, and the range's second mode-flag bit means a version 9 host reads a player
 * asking for a bandage as a player asking for nothing.
 *
 * Moved to 11 with the weapon rack: `WEAPON_ORDER` grew from five guns to eleven and none of the
 * old positions survived, so a version 10 peer reads every weapon byte as a different gun.
 */
export const MIN_COMPATIBLE_VERSION = 11;

export type VersionVerdict = 'ok' | 'peerTooOld' | 'peerTooNew';

/** Both sides run this against each other and reach opposite, consistent verdicts. */
export function negotiate(remoteVersion: number): VersionVerdict {
  if (remoteVersion < MIN_COMPATIBLE_VERSION) return 'peerTooOld';
  if (remoteVersion > PROTOCOL_VERSION) return 'peerTooNew';
  return 'ok';
}

/** What to tell the player, given which side is behind. */
export function versionVerdictDetail(verdict: VersionVerdict, remoteVersion: number): string {
  const versions = `theirs ${String(remoteVersion)}, ours ${String(PROTOCOL_VERSION)}`;
  if (verdict === 'peerTooOld') return `they need to reload the page (${versions})`;
  if (verdict === 'peerTooNew') return `this tab needs to be reloaded (${versions})`;
  return versions;
}
